import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { IconListDetails } from '@tabler/icons-react';

import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { ViewExternalIdBadgeList } from '#plugin-chat/components/tool-display/ViewExternalIdBadge.tsx';
import type { DescribeViewsInput, DescribeViewsOutput } from '#plugin-chat/components/tools/describe-views/schema.ts';
import type { ToolProgressComponent } from '#plugin-chat/lib/tool/progress-registry.ts';

/**
 * UI component for {@link DescribeViewsTool}.
 *
 * Renders a compact badge list of the requested view externalIds so the user
 * can see which views the agent introspected without expanding the message.
 */
export function RenderDescribeViewsTool({ args }: ToolCallMessagePartProps<DescribeViewsInput, DescribeViewsOutput>) {
  const requestedViews = args?.views ?? [];

  return (
    <ToolDisplay>
      <ToolDisplay.Running>
        <span>Fetching view schema...</span>
        <ViewExternalIdBadgeList externalIds={requestedViews} />
      </ToolDisplay.Running>
      <ToolDisplay.Complete color="var(--mantine-color-grape-3)" icon={IconListDetails}>
        <div>
          <span>View schema fetched</span>
          <ViewExternalIdBadgeList externalIds={requestedViews} />
        </div>
      </ToolDisplay.Complete>
    </ToolDisplay>
  );
}
/**
 * Progress component for DescribeViewsTool.
 *
 * Rendered inside the "AiPSO Agent is ..." progress bubble while the tool is running.
 */
export const ProgressDescribeViewsTool: ToolProgressComponent<DescribeViewsInput, DescribeViewsOutput> = ({ args }) => {
  const count = args?.views?.length ?? 0;
  if (count === 0) {
    return <>fetching view schema...</>;
  }
  return (
    <>
      fetching {count} view schema{count === 1 ? '' : 's'}...
    </>
  );
};
